import { clamp, clamp01 } from '@/core/math/scalar';

/**
 * Audio.
 *
 * Everything here is synthesised. There are no sample files, for the same
 * reason there are no terrain textures: the parameters that decide what a thing
 * sounds like are already in the simulation, and driving an oscillator from
 * them gives a continuum where a sample library gives a handful of fixed
 * states. An engine at 12% throttle in thin air genuinely sounds different from
 * one at 90% in thick air, without anyone authoring either.
 *
 * The rule that shapes the whole design: **sound needs a medium**. In vacuum
 * the only things audible are the ones mechanically coupled to the hull — the
 * engines through their mounts, pumps, the reactor, the air handling inside the
 * cabin. Nothing outside the vessel makes a sound in space, ever. Enter an
 * atmosphere and airflow appears, rising with dynamic pressure, and the engines
 * gain the airborne component they were missing.
 *
 * That is not a stylistic choice, it is the same physics the aerodynamics model
 * already computes, so the transition happens on its own during an entry: the
 * wind comes up as the air thickens and the vessel slows, and it goes silent
 * again on the way out.
 */

export interface AudioConditions {
  /** Ambient air density, kg/m^3. Zero in vacuum. */
  density: number;
  /** Dynamic pressure on the hull, pascals. Drives airflow noise. */
  dynamicPressure: number;
  /** Speed through the air, m/s. */
  airspeed: number;
  /** Mach number, for the transonic rumble. */
  mach: number;
  /** Main engine throttle, 0..1. */
  throttle: number;
  /** Total thrust, newtons. */
  thrust: number;
  /** RCS activity, 0..1. */
  rcs: number;
  /** Field drive output, m/s^2. */
  fieldDrive: number;
  /** Electrical load, watts. Drives the cabin hum. */
  power: number;
  /** True when the player is outside the vessel. */
  onFoot: boolean;
  /** True when a pressurised cabin is around the player. */
  inCabin: boolean;
  /** Surface contact, for the settling creak and ground rumble. */
  contacts: number;
  /** Wind speed of the local air mass, m/s. */
  windSpeed: number;
}

/** One synthesised voice: a source, a filter and a gain the mixer can ride. */
interface Voice {
  gain: GainNode;
  filter: BiquadFilterNode;
  source: AudioScheduledSourceNode;
  /** Only present for tonal voices. */
  osc?: OscillatorNode;
}

/**
 * Builds a buffer of pink-ish noise.
 *
 * White noise sounds thin and hissy; real airflow and combustion have most of
 * their energy at low frequencies. This is the standard Voss-McCartney style
 * approximation, cheap and good enough that the filters do the rest.
 */
function createNoiseBuffer(context: AudioContext, seconds = 4): AudioBuffer {
  const length = Math.floor(context.sampleRate * seconds);
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const data = buffer.getChannelData(0);
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  for (let i = 0; i < length; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99765 * b0 + white * 0.099046;
    b1 = 0.963 * b1 + white * 0.2965164;
    b2 = 0.57 * b2 + white * 1.0526913;
    data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.22;
  }
  return buffer;
}

export class AudioSystem {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;

  private wind: Voice | null = null;
  private engine: Voice | null = null;
  private rumble: Voice | null = null;
  private cabin: Voice | null = null;
  private drive: Voice | null = null;
  private rcs: Voice | null = null;

  /** Master volume, 0..1. */
  volume = 0.7;
  private muted = false;
  /**
   * Silenced by the game rather than by the player — currently free flight,
   * where the camera is detached from the vessel entirely and the vessel's own
   * noise has nothing to do with what is on screen.
   *
   * Deliberately separate from `muted`: folding this into the player's own mute
   * would mean leaving free flight unmutes audio the player had muted by hand.
   */
  private suspended = false;
  private started = false;

  get running(): boolean {
    return this.started && this.context?.state === 'running';
  }

  /**
   * Creates the graph. Must be called from a user gesture: browsers refuse to
   * start an AudioContext otherwise, and a silently suspended context is worse
   * than no audio because nothing reports it.
   */
  start(): void {
    if (this.started) {
      void this.context?.resume();
      return;
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    try {
      const context = new Ctor();
      this.context = context;
      this.noise = createNoiseBuffer(context);

      const master = context.createGain();
      master.gain.value = 0;
      master.connect(context.destination);
      this.master = master;

      // Airflow over the hull: broadband, brightening with speed.
      this.wind = this.makeNoiseVoice(context, master, 'lowpass', 400);
      // Engine: filtered noise for the combustion roar...
      this.engine = this.makeNoiseVoice(context, master, 'bandpass', 180);
      // ...plus a low tone for the turbopump.
      this.rumble = this.makeToneVoice(context, master, 'sawtooth', 42);
      // Cabin: the constant electrical and life-support hum, present whenever
      // the player is inside a pressurised hull, including in vacuum.
      this.cabin = this.makeToneVoice(context, master, 'triangle', 96);
      // Field drive: a clean high tone, deliberately unlike anything chemical.
      this.drive = this.makeToneVoice(context, master, 'sine', 320);
      // RCS: short bursts of hiss.
      this.rcs = this.makeNoiseVoice(context, master, 'highpass', 1400);

      this.started = true;
      void context.resume();
    } catch (err) {
      console.warn('[AudioSystem] could not start audio', err);
      this.context = null;
      this.started = false;
    }
  }

  private makeNoiseVoice(
    context: AudioContext,
    destination: AudioNode,
    type: BiquadFilterType,
    frequency: number,
  ): Voice {
    const source = context.createBufferSource();
    source.buffer = this.noise;
    source.loop = true;
    const filter = context.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = frequency;
    filter.Q.value = 0.8;
    const gain = context.createGain();
    gain.gain.value = 0;
    source.connect(filter).connect(gain).connect(destination);
    source.start();
    return { gain, filter, source };
  }

  private makeToneVoice(
    context: AudioContext,
    destination: AudioNode,
    type: OscillatorType,
    frequency: number,
  ): Voice {
    const osc = context.createOscillator();
    osc.type = type;
    osc.frequency.value = frequency;
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 900;
    const gain = context.createGain();
    gain.gain.value = 0;
    osc.connect(filter).connect(gain).connect(destination);
    osc.start();
    return { gain, filter, source: osc, osc };
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  /** Silences everything without touching the player's own mute setting. */
  setSuspended(suspended: boolean): void {
    this.suspended = suspended;
  }

  toggleMuted(): boolean {
    this.muted = !this.muted;
    return this.muted;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /**
   * Rides every voice from the current simulation state.
   *
   * Called once per frame with the real frame delta. All changes are ramped
   * rather than assigned, because a gain that jumps produces an audible click
   * on every single frame.
   */
  update(c: AudioConditions, dt: number): void {
    const context = this.context;
    const master = this.master;
    if (!context || !master || context.state !== 'running') return;

    const now = context.currentTime;
    const ramp = (param: AudioParam, target: number): void => {
      // A short time constant: fast enough to follow a throttle, slow enough
      // that no step is audible as a click.
      param.setTargetAtTime(target, now, Math.max(0.02, Math.min(0.25, dt * 4)));
    };

    ramp(master.gain, this.muted || this.suspended ? 0 : clamp01(this.volume));

    // --- The medium ---------------------------------------------------------
    // Everything outside the hull is scaled by this. In vacuum it is zero and
    // the outside world is silent, which is the whole point.
    const medium = clamp01(c.density / 0.35);

    // --- Airflow ------------------------------------------------------------
    // Dynamic pressure is what the hull actually feels, so it is what the wind
    // is driven by. On foot the ambient breeze stands in for it.
    const flow = c.onFoot
      ? clamp01(c.windSpeed / 30) * medium
      : clamp01(c.dynamicPressure / 12000) * medium;
    if (this.wind) {
      ramp(this.wind.gain.gain, flow * 0.5);
      // Faster flow is brighter: more energy at high frequencies.
      ramp(this.wind.filter.frequency, 260 + clamp01(c.airspeed / 900) * 5200);
    }

    // --- Engines ------------------------------------------------------------
    // Structure-borne through the mounts, so it is audible in vacuum. The
    // airborne component on top of it is not.
    const structural = clamp01(c.throttle) * (c.inCabin || !c.onFoot ? 1 : 0);
    const airborne = structural * medium;
    if (this.engine) {
      ramp(this.engine.gain.gain, structural * 0.16 + airborne * 0.26);
      ramp(this.engine.filter.frequency, 120 + clamp01(c.thrust / 400000) * 900);
    }
    if (this.rumble) {
      ramp(this.rumble.gain.gain, structural * 0.2 + airborne * 0.12);
      if (this.rumble.osc) ramp(this.rumble.osc.frequency, 34 + clamp01(c.throttle) * 46);
    }

    // Transonic buffet: only meaningful where there is air to shock.
    if (this.wind && c.mach > 0.8) {
      const buffet = clamp01((c.mach - 0.8) / 0.5) * medium;
      ramp(this.wind.gain.gain, flow * 0.5 + buffet * 0.25);
    }

    // --- RCS ----------------------------------------------------------------
    if (this.rcs) ramp(this.rcs.gain.gain, clamp01(c.rcs) * (0.05 + medium * 0.05));

    // --- Field drive --------------------------------------------------------
    if (this.drive) {
      const output = clamp01(c.fieldDrive / 12);
      ramp(this.drive.gain.gain, output * 0.055);
      if (this.drive.osc) ramp(this.drive.osc.frequency, 240 + output * 420);
    }

    // --- Cabin --------------------------------------------------------------
    // The only thing that is louder in vacuum: with no airflow to mask it, the
    // hull's own machinery is all there is to hear.
    if (this.cabin) {
      const inside = c.inCabin ? 1 : 0;
      const quietBonus = 1 - medium * 0.45;
      ramp(this.cabin.gain.gain, inside * (0.028 + clamp01(c.power / 400000) * 0.03) * quietBonus);
      if (this.cabin.osc) ramp(this.cabin.osc.frequency, 88 + clamp(c.power / 40000, 0, 22));
    }
  }

  dispose(): void {
    for (const voice of [this.wind, this.engine, this.rumble, this.cabin, this.drive, this.rcs]) {
      if (!voice) continue;
      try {
        voice.source.stop();
      } catch {
        // Already stopped; nothing to do.
      }
      voice.gain.disconnect();
    }
    void this.context?.close();
    this.context = null;
    this.started = false;
  }
}
